import React, { useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiEye,
  FiEyeOff,
  FiImage,
  FiMove,
  FiPlus,
  FiRefreshCcw,
  FiSave,
  FiStar,
  FiTrash2,
} from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaAlbumServices";

import AdminButton from "./../../_shared/ui/AdminButton";
import AdminDrawer from "./../../_shared/ui/AdminDrawer";

import { moveArrayItem } from "../utils/mediaAlbumsAdminUtils";

export default function MediaAlbumPhotosDrawer({
  open,
  album,
  actionId,
  onClose,
  onOpenMediaPicker,
  onSetCover,
  onToggleItemVisible,
  onRemoveItem,
  onUpdateItem,
  onReorderItems,
  onRefresh,
}) {
  const [localItems, setLocalItems] = useState([]);
  const [draggedId, setDraggedId] = useState(null);
  const [editingCaptionId, setEditingCaptionId] = useState(null);
  const [captionDraft, setCaptionDraft] = useState("");

  useEffect(() => {
    const nextItems = Array.isArray(album?.items) ? album.items : [];
    setLocalItems(nextItems);
  }, [album]);

  const visibleCount = useMemo(() => {
    return localItems.filter((item) => !!item.is_visible).length;
  }, [localItems]);

  const coverMediaId = Number(album?.cover_media_id || 0);

  const startEditCaption = (item) => {
    setEditingCaptionId(item.id);
    setCaptionDraft(item.caption || "");
  };

  const cancelEditCaption = () => {
    setEditingCaptionId(null);
    setCaptionDraft("");
  };

  const saveCaption = async (item) => {
    await onUpdateItem?.(item.id, {
      caption: captionDraft,
    });

    setEditingCaptionId(null);
    setCaptionDraft("");
  };

  const handleDragStart = (itemId) => {
    setDraggedId(itemId);
  };

  const handleDragOver = (event, targetItemId) => {
    event.preventDefault();

    if (!draggedId || draggedId === targetItemId) return;

    setLocalItems((previous) => {
      const fromIndex = previous.findIndex((item) => item.id === draggedId);
      const toIndex = previous.findIndex((item) => item.id === targetItemId);

      if (fromIndex === -1 || toIndex === -1) return previous;

      return moveArrayItem(previous, fromIndex, toIndex);
    });
  };

  const handleDrop = async () => {
    if (!draggedId) return;

    const orderedIds = localItems
      .map((item) => Number(item.id))
      .filter((id) => Number.isInteger(id) && id > 0);

    setDraggedId(null);

    if (orderedIds.length > 0) {
      await onReorderItems?.(orderedIds);
    }
  };

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title="Photos de l’album"
      subtitle={
        album?.title
          ? `Organisez les photos de “${album.title}”.`
          : "Organisez les photos de cet album."
      }
      icon={FiImage}
      size="full"
      zIndex="z-[90]"
      footer={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
            {localItems.length} photo(s), {visibleCount} visible(s)
          </p>

          <div className="flex flex-wrap gap-2">
            <AdminButton
              icon={FiRefreshCcw}
              variant="secondary"
              loading={actionId === "refresh"}
              onClick={onRefresh}
            >
              Actualiser
            </AdminButton>

            <AdminButton icon={FiPlus} variant="primary" onClick={onOpenMediaPicker}>
              Ajouter des photos
            </AdminButton>

            <AdminButton variant="dark" onClick={onClose}>
              Fermer
            </AdminButton>
          </div>
        </div>
      }
    >
      {!album ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Album introuvable.
        </p>
      ) : (
        <div className="space-y-5">
          <div className="rounded-[1.7rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                  {album.title}
                </h3>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Glissez-déposez les photos pour modifier l’ordre du carrousel.
                  Cliquez sur l’étoile pour choisir la couverture.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {album.show_on_home ? (
                  <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-bold text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">
                    <FiStar />
                    Carrousel accueil
                  </span>
                ) : null}

                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                  <FiImage />
                  {localItems.length} photo(s)
                </span>
              </div>
            </div>
          </div>

          {localItems.length === 0 ? (
            <div className="flex min-h-[360px] items-center justify-center rounded-[1.7rem] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950">
              <div>
                <FiImage className="mx-auto text-5xl text-slate-300 dark:text-slate-600" />

                <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
                  Aucune photo dans cet album
                </h3>

                <p className="mt-2 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Ajoutez des photos depuis la médiathèque pour composer le
                  carrousel.
                </p>

                <div className="mt-5">
                  <AdminButton icon={FiPlus} variant="primary" onClick={onOpenMediaPicker}>
                    Ajouter des photos
                  </AdminButton>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {localItems.map((item, index) => {
                const media = item.media || {};
                const imageUrl = toAbsolutePublicUrl(media.url);
                const isCover = Number(item.media_id) === coverMediaId;
                const isDragging = draggedId === item.id;
                const isEditingCaption = editingCaptionId === item.id;

                return (
                  <article
                    key={item.id}
                    draggable
                    onDragStart={() => handleDragStart(item.id)}
                    onDragOver={(event) => handleDragOver(event, item.id)}
                    onDrop={handleDrop}
                    onDragEnd={() => setDraggedId(null)}
                    className={`group overflow-hidden rounded-[1.7rem] border bg-white shadow-sm transition dark:bg-slate-900 ${
                      isDragging
                        ? "scale-[0.98] border-sky-300 opacity-60 ring-4 ring-sky-100 dark:border-sky-500 dark:ring-sky-500/10"
                        : "border-slate-200 hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-xl hover:shadow-slate-200/60 dark:border-slate-800 dark:hover:border-sky-500/30 dark:hover:shadow-black/20"
                    }`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-800">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={
                            item.alt_text_override ||
                            media.alt_text ||
                            media.title ||
                            "Photo de l’album"
                          }
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-slate-400">
                          <FiImage className="text-4xl" />
                        </div>
                      )}

                      <div className="absolute left-3 top-3 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-950/75 px-2.5 py-1 text-xs font-bold text-white backdrop-blur">
                          <FiMove />
                          {index + 1}
                        </span>

                        {isCover ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 text-xs font-bold text-white shadow-lg">
                            <FiStar />
                            Couverture
                          </span>
                        ) : null}
                      </div>

                      {!item.is_visible ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-slate-950/55 text-white backdrop-blur-[1px]">
                          <div className="text-center">
                            <FiEyeOff className="mx-auto text-3xl" />
                            <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em]">
                              Masquée
                            </p>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="space-y-3 p-4">
                      <div>
                        <p className="line-clamp-2 text-sm font-bold text-slate-950 dark:text-white">
                          {item.caption ||
                            media.title ||
                            media.original_name ||
                            media.file_name ||
                            "Photo sans titre"}
                        </p>

                        <p className="mt-1 line-clamp-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                          {media.category || "Sans catégorie"}
                        </p>
                      </div>

                      {isEditingCaption ? (
                        <div className="space-y-2">
                          <textarea
                            value={captionDraft}
                            onChange={(event) => setCaptionDraft(event.target.value)}
                            rows={3}
                            placeholder="Légende de la photo..."
                            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                          />

                          <div className="flex gap-2">
                            <AdminButton
                              icon={FiSave}
                              variant="primary"
                              size="sm"
                              loading={actionId === `update-${item.id}`}
                              onClick={() => saveCaption(item)}
                            >
                              Enregistrer
                            </AdminButton>

                            <AdminButton
                              variant="secondary"
                              size="sm"
                              onClick={cancelEditCaption}
                            >
                              Annuler
                            </AdminButton>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <AdminButton
                            icon={FiStar}
                            variant={isCover ? "secondary" : "soft"}
                            size="sm"
                            loading={actionId === `cover-${item.media_id}`}
                            disabled={isCover}
                            onClick={() => onSetCover?.(item.media_id)}
                          >
                            {isCover ? "Couverture" : "Couverture"}
                          </AdminButton>

                          <AdminButton
                            icon={item.is_visible ? FiEyeOff : FiEye}
                            variant="secondary"
                            size="sm"
                            loading={actionId === `visible-${item.id}`}
                            onClick={() => onToggleItemVisible?.(item)}
                          >
                            {item.is_visible ? "Masquer" : "Afficher"}
                          </AdminButton>

                          <AdminButton
                            icon={FiCheckCircle}
                            variant="soft"
                            size="sm"
                            onClick={() => startEditCaption(item)}
                          >
                            Légende
                          </AdminButton>

                          <AdminButton
                            icon={FiTrash2}
                            variant="dangerSoft"
                            size="sm"
                            loading={actionId === `remove-${item.id}`}
                            onClick={() => onRemoveItem?.(item)}
                          >
                            Retirer
                          </AdminButton>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      )}
    </AdminDrawer>
  );
}